import { QuizEvaluationDTO } from '../models/student/quiz/quiz-evaluation.interface';
import {
  DismissFeedbackDTO,
  FeedbackPromptDTO,
  SubmitFeedbackDTO,
} from '../models/student/feedback-prompt.interface';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { StudentCompanyCourseArticleService } from '../infrastructure/student/student-company-course-article.service';
import { StudentCompanyCourseService } from '../infrastructure/student/student-company-course.service';
import { StudentCompanyCourseDetailDTO } from '../models/student/student-company-course-detail.interface';
import { StudentCompanyCourseDTO } from '../models/student/student-company-course.interface';
import { StudentCourseArticleCreateAnswerDTO } from '../models/student/student-course-article-create-answer.interface';
import { StudentCourseArticleCreateFeedbackDTO } from '../models/student/student-course-article-create-feedback.interface';
import { StudentCourseArticleDTO } from '../models/student/student-course-article.interface';
import { StudentCourseTreeDTO } from '../models/student/student-course-tree.interface';
import { StudentWidgetService } from '../infrastructure/student/student-widget.service';
import { HotspotResultDTO, StudentClosedPollDTO, StudentPollStateDTO, StudentWidgetDTO } from '../models/widgets/student-widget.interface';
import { WidgetEventTypeEnum } from '../models/widgets/widget-event-type.enum';

@Injectable({ providedIn: 'root' })
export class StudentCompanyCourseFacade {
  constructor(
     private studentCompanyCourseService: StudentCompanyCourseService,
     private studentCourseArticleService: StudentCompanyCourseArticleService,
     private studentWidgetService: StudentWidgetService,
  ) { }


  getCourses(): Observable<StudentCompanyCourseDTO[]> {
    return this.studentCompanyCourseService.getCourses();
  }

  /** Všechny kurzy pro profil — včetně archivovaných a doběhlých. */
  getAllCourses(): Observable<StudentCompanyCourseDTO[]> {
    return this.studentCompanyCourseService.getAllCourses();
  }

  getCourse(courseId: string): Observable<StudentCompanyCourseDetailDTO> {
    return this.studentCompanyCourseService.getCourse(courseId);
  }

  /** ⚠ Bere articleMetaId, ne id článku. */
  getArticleWidgets(articleMetaId: string): Observable<StudentWidgetDTO[]> {
    return this.studentWidgetService.getForArticle(articleMetaId);
  }

  /**
   * Uzavřené ankety kurzu, u kterých student hlasoval a výsledek ještě neviděl.
   *
   * ⚠ Bere courseMetaId, ne id kurzu.
   */
  getClosedPolls(courseMetaId: string): Observable<StudentClosedPollDTO[]> {
    return this.studentWidgetService.getClosedPolls(courseMetaId);
  }

  trackWidgetEvent(widgetId: string, eventType: WidgetEventTypeEnum): Observable<void> {
    return this.studentWidgetService.trackEvent(widgetId, eventType);
  }

  trackWidgetEvents(widgetIds: string[], eventType: WidgetEventTypeEnum): Observable<void> {
    return this.studentWidgetService.trackEvents(widgetIds, eventType);
  }

  getPollState(widgetId: string): Observable<StudentPollStateDTO> {
    return this.studentWidgetService.getPollState(widgetId);
  }

  voteInPoll(widgetId: string, optionKeys: string[]): Observable<StudentPollStateDTO> {
    return this.studentWidgetService.vote(widgetId, optionKeys);
  }

  retractPollVote(widgetId: string): Observable<StudentPollStateDTO> {
    return this.studentWidgetService.retractVote(widgetId);
  }

  clickHotspot(widgetId: string, spotId: string): Observable<HotspotResultDTO> {
    return this.studentWidgetService.clickHotspot(widgetId, spotId);
  }

  getCourseTree(courseId: string): Observable<StudentCourseTreeDTO> {
    return this.studentCompanyCourseService.getCourseTree(courseId);
  }

  getArticle(articleId: string): Observable<StudentCourseArticleDTO> {
    return this.studentCourseArticleService.getArticle(articleId);
  }

  getFeedbackPrompt(articleMetaId: string): Observable<FeedbackPromptDTO | null> {
    return this.studentCourseArticleService.getFeedbackPrompt(articleMetaId);
  }

  submitFeedbackPrompt(articleMetaId: string, data: SubmitFeedbackDTO): Observable<void> {
    return this.studentCourseArticleService.submitFeedbackPrompt(articleMetaId, data);
  }

  dismissFeedbackPrompt(articleMetaId: string, data: DismissFeedbackDTO): Observable<void> {
    return this.studentCourseArticleService.dismissFeedbackPrompt(articleMetaId, data);
  }

  createFeedback(articleId: string, data: StudentCourseArticleCreateFeedbackDTO): Observable<void> {
    return this.studentCourseArticleService.createFeedback(articleId, data);
  }

  addReadingTime(articleId: string, seconds: number): Observable<void> {
    return this.studentCourseArticleService.addReadingTime(articleId, seconds);
  }

  createStatistics(articleId: string): Observable<void> {
    return this.studentCourseArticleService.createStatistics(articleId);
  }

  createAnswers(articleId: string, data: StudentCourseArticleCreateAnswerDTO[]): Observable<void> {
    return this.studentCourseArticleService.createAnswers(articleId, data);
  }

  evaluateQuiz(articleMetaId: string, questionId: string, selectedAnswerIds: string[]): Observable<QuizEvaluationDTO> {
    return this.studentCourseArticleService.evaluateQuiz(articleMetaId, questionId, selectedAnswerIds);
  }

  clearAnswers(articleId: string): Observable<void> {
    return this.studentCourseArticleService.clearAnswers(articleId);
  }

  registerSectionExpanded(articleSectionId: string): Observable<void> {
    return this.studentCourseArticleService.registerSectionExpanded(articleSectionId);
  }
}
